import b4w from "blend4web";
import style from "../scss/webplayer.scss";

const m_app = b4w.app;
const m_camera_anim = b4w.camera_anim;
const m_camera = b4w.camera;
const m_cfg = b4w.config;
const m_cont = b4w.container;
const m_ctl = b4w.controls;
const m_data = b4w.data;
const m_gp_conf = b4w.gp_conf;
const m_input = b4w.input;
const m_main = b4w.main;
const m_scs = b4w.scenes;
const m_storage = b4w.storage;
const m_version = b4w.version;

const DEFAULT_LANG = "en";
const BUILT_IN_SCRIPTS_ID = "built_in_scripts";
const CIRCUMFERENCE = 2 * Math.PI * 54;
const DEFAULT_CAM_TYPE = m_camera.MS_STATIC;


let _nla_fps = 24;
let _no_social = false;
let _socials = [];
let _lang = DEFAULT_LANG;

let _preloader_cont;
let _logo_cont;
let _circle;
let _selected_object;
let _canvas_cont;
let _pers_loaded;

let _vec2_tmp = new Float32Array(2);
let _vec3_tmp = new Float32Array(3);
let _vec3_tmp2 = new Float32Array(3);
let _quat_tmp = new Float32Array(4);

let _is_touch = false;
let _is_down = false;
let _pick = m_scs.pick_object;
let _active_elem = null;
let _active_cb = null;
let _window_width = 0;
let _focus_cb = function () {};
let _blur_cb = function () {};

const LOAD_PARAM_STR = "__ASSETS_LOADING_PATH__";
const PVR_PARAM_STR = "__COMPRESSED_TEXTURES_PVR__";
const DDS_PARAM_STR = "__COMPRESSED_TEXTURES_DDS__";
const FPS_PARAM_STR = "__SHOW_FPS__";
const SOCIAL_PARAM_STR = "__NO_SOCIAL__";
const ALPHA_PARAM_STR = "__ALPHA__";
const MIN_CAP_PARAM_STR = "__MIN_CAPABILITIES__";
const AUTOROTATE_PARAM_STR = "__AUTOROTATE__";
const GZIP_PARAM_STR = "__COMPRESSED_GZIP__";

const _url_params = {
    "load": LOAD_PARAM_STR,
    "compressed_textures_pvr": PVR_PARAM_STR,
    "compressed_textures_dds": DDS_PARAM_STR,
    "show_fps": FPS_PARAM_STR,
    "no_social": SOCIAL_PARAM_STR,
    "alpha": ALPHA_PARAM_STR,
    "min_capabilities": MIN_CAP_PARAM_STR,
    "autorotate": AUTOROTATE_PARAM_STR,
    "compressed_gzip": GZIP_PARAM_STR
};


const init = function () {

    let is_debug = (m_version.type() == "DEBUG");
    let is_html = b4w.module_check(m_cfg.get("built_in_module_name"));

    let url_params = m_app.get_url_params();

    let load = "";

    if (url_params && _url_params["load"] != LOAD_PARAM_STR)
        load = _url_params["load"];
    else
        load = window.location.href;

    let show_fps = _url_params["show_fps"] != FPS_PARAM_STR;
    let allow_cors = check_cors();
    let alpha = _url_params["alpha"] != ALPHA_PARAM_STR;
    let dds_available = _url_params["compressed_textures_dds"] != DDS_PARAM_STR;
    let min_capabilities = _url_params["min_capabilities"] != MIN_CAP_PARAM_STR;
    let pvr_available = _url_params["compressed_textures_pvr"] != PVR_PARAM_STR;
    let gzip_available = _url_params["compressed_gzip"] != GZIP_PARAM_STR;

    _no_social = _url_params["no_social"] != SOCIAL_PARAM_STR;

    let min50_available = false;

    if ((dds_available || pvr_available) && !is_html && !is_debug)
        min50_available = true;

    if (url_params) {
        if (!is_html && !is_debug) {
            if (!dds_available && "compressed_textures" in url_params) {
                min50_available = true;
                dds_available = true;
            }

            if (!pvr_available && "compressed_textures_pvr" in url_params) {
                min50_available = true;
                pvr_available = true;
            }

            if (!gzip_available && "compressed_gzip" in url_params)
                gzip_available = true;
        }

        if (!alpha && "alpha" in url_params)
            alpha = true;

        if ("load" in url_params)
            load = url_params["load"];

        if (!min_capabilities && "min_capabilities" in url_params)
            min_capabilities = true;
    }

    m_storage.init("b4w_webplayer:" + load);

    cache_dom_elems();

    if (!alpha)
        m_cfg.set("background_color", [0.224, 0.224, 0.224, 1.0]);

    // disable physics in HTML version
    m_app.init({
        canvas_container_id: "main_canvas_cont",
        callback: init_cb,
        gl_debug: is_debug,
        physics_enabled: !is_html,
        show_fps: show_fps,
        report_init_failure: false,
        console_verbose: is_debug,
        error_purge_elements: ["control_panel"],
        alpha: true,
        allow_cors: allow_cors,
        key_pause_enabled: false,
        fps_elem_id: "fps_cont",
        fps_wrapper_id: "fps_cont",
        assets_pvr_available: pvr_available,
        assets_dds_available: dds_available,
        assets_min50_available: min50_available,
        min_capabilities: min_capabilities,
        assets_gzip_available: gzip_available,
        shaders_path: "shaders/"
    })
}

function init_cb(canvas_element, success) {
    check_preview_image();

    if (!success) {
        display_no_webgl_bg();

        return;
    }

    _window_width = window.innerWidth;

    m_main.pause();

    check_touch();

    check_cors();

    m_gp_conf.update();

    let file = search_file();

    if (!file)
        return;

    anim_logo(file);

    window.addEventListener("resize", on_resize);

    on_resize();
}

function check_cors() {
    let url_params = m_app.get_url_params();

    if (url_params && "allow_cors" in url_params) { return true; }

    return false;
}

function check_touch() {
    _is_touch = "ontouchstart" in window || navigator.maxTouchPoints;
}

function check_preview_image() {
    let url_params = m_app.get_url_params();

    if (!url_params)
        return;

    let bg = "";

    if ("preview_image" in url_params)
        bg = url_params["preview_image"];
    else
        return;

    _preloader_cont.querySelector("#bg").style.backgroundImage = "url(" + bg + ")";
}

function display_no_webgl_bg() {
    const url_params = m_app.get_url_params(true);

    if (url_params && url_params["fallback_image"]) {
        const image_wrapper = document.createElement("div");

        image_wrapper.className = "image_wrapper";
        image_wrapper.style.backgroundImage = "url(" + url_params["fallback_image"] + ")";

        _preloader_cont.style.display = "none";
        document.body.appendChild(image_wrapper);
    } else if (url_params && url_params["fallback_video"]) {
        const video_wrapper = document.createElement("div");
        const video_elem = document.createElement("video");

        video_wrapper.className = "video_wrapper";
        video_elem.autoplay = true;

        for (let i = 0; i < url_params["fallback_video"].length; i++) {
            const source = document.createElement("source");
            source.src = url_params["fallback_video"][i];

            video_elem.appendChild(source);
        }

        video_wrapper.appendChild(video_elem);

        _preloader_cont.style.display = "none";

        document.body.appendChild(video_wrapper);
    } else
        show_error("Browser could not initialize WebGL", "For more info visit",
            "https://www.blend4web.com/doc/en/problems_and_solutions.html#problems-upon-startup");
}

function cache_dom_elems() {
    _preloader_cont = document.querySelector("#preloader_cont");
    _pers_loaded = document.querySelector("#logo_cont span");
    _canvas_cont = document.querySelector("#main_canvas_cont");
    _logo_cont = document.querySelector("#logo_cont");
    _circle = _logo_cont.querySelector("circle");

    _circle.style["strokeDasharray"] = CIRCUMFERENCE;
    _circle.style["strokeDashoffset"] = CIRCUMFERENCE;
    _circle.setAttribute("transform", _circle.getAttribute("transform"));
}

function search_file() {
    let module_name = m_cfg.get("built_in_module_name");
    let file = "";

    if (b4w.module_check(module_name)) {
        let bd = b4w.require(module_name);
        file = bd["data"]["main_file"];

        remove_built_in_scripts();

        return file;
    } else {
        let url_params = m_app.get_url_params();

        if (url_params && url_params["load"]) {
            file = url_params["load"];

            return file;
        } else if (url_params && _url_params["load"] && _url_params["load"] != "__ASSETS_LOADING_PATH__") {
            file = _url_params["load"];

            return file;
        } else {
            show_error("Please specify a scene to load");
            return null;
        }
    }
}

function anim_logo(file) {
    _logo_cont.classList.add("active");

    setTimeout(function () {
        m_main.resume();
        m_data.load(file, loaded_callback, preloader_callback, false);
    }, 800);
}

function on_resize() {
    m_cont.resize_to_container();
    _window_width = window.innerWidth;
}

function get_selected_object() {
    return _selected_object;
}

function set_selected_object(obj) {
    _selected_object = obj;
}

function mouse_cb() {
    if (!m_scs.can_select_objects())
        return;

    const canvas_elem = m_cont.get_canvas();
    const mdevice = m_input.get_device_by_type_element(m_input.DEVICE_MOUSE, canvas_elem);
    let loc = m_input.get_vector_param(mdevice, m_input.MOUSE_LOCATION, _vec2_tmp);
    main_canvas_clicked(loc[0], loc[1]);
}

function touch_cb(touches) {
    if (!m_scs.can_select_objects())
        return;

    for (var i = 0; i < touches.length; i++)
        main_canvas_clicked(touches[i].clientX, touches[i].clientY);
}

function register_canvas_click() {
    const canvas_elem = m_cont.get_canvas();
    const mdevice = m_input.get_device_by_type_element(m_input.DEVICE_MOUSE, canvas_elem);
    const tdevice = m_input.get_device_by_type_element(m_input.DEVICE_TOUCH, canvas_elem);

    if (mdevice)
        m_input.attach_param_cb(mdevice, m_input.MOUSE_DOWN_WHICH, mouse_cb);

    if (tdevice)
        m_input.attach_param_cb(tdevice, m_input.TOUCH_START, touch_cb);
}

function main_canvas_clicked(x, y) {
    let prev_obj = get_selected_object();

    if (prev_obj && m_scs.outlining_is_enabled(prev_obj) && prev_obj.render.outline_on_select)
        m_scs.clear_outline_anim(prev_obj);

    let obj = _pick(x, y);
    set_selected_object(obj);
}

function loaded_callback(data_id, success) {
    if (!success) {
        show_error("Could not load the scene");
        return;
    }

    register_canvas_click();

    m_app.enable_camera_controls(false, false, false, null, true);

    var mouse_move = m_ctl.create_mouse_move_sensor();
    var mouse_click = m_ctl.create_mouse_click_sensor();

    var canvas_cont = m_cont.get_container();

    function move_cb() {
        canvas_cont.className = "move";
    }

    function stop_cb(obj, id, pulse) {
        if (pulse == -1)
            canvas_cont.className = "";
    }

    m_ctl.create_sensor_manifold(null, "MOUSE_MOVE", m_ctl.CT_SHOT, [mouse_click, mouse_move], function (s) {
        return s[0] && s[1]
    }, move_cb);

    m_ctl.create_sensor_manifold(null, "MOUSE_STOP", m_ctl.CT_TRIGGER, [mouse_click], function (s) {
        return s[0]
    }, stop_cb);
}

function preloader_callback(percentage, load_time) {
    let dashoffset = CIRCUMFERENCE * (1 - percentage / 100);
    _circle.style["strokeDashoffset"] = dashoffset;

    _pers_loaded.innerHTML = percentage.toString();

    if (percentage == 100) {
        _logo_cont.classList.remove("active");
        _preloader_cont.classList.remove("active");
        setTimeout(function () {
            _preloader_cont.parentNode.removeChild(_preloader_cont);
        }, 1200)
    }
}

function remove_built_in_scripts() {
    let scripts = document.getElementById(BUILT_IN_SCRIPTS_ID);
    scripts.parentElement.removeChild(scripts);
}

function reload_app() {
    setTimeout(function () {
        window.location.reload();
    }, 100);
}

function show_error(text_message, link_message, link) {
    let error_name = document.querySelector("#error_name");
    let error_info = document.querySelector("#error_info");
    let error_cont = document.querySelector("#error_cont");

    error_name.innerHTML = text_message;
    error_info.innerHTML = `${link_message} <a href="${link}">${link.replace("https://www.", "")}</a>`;
    error_cont.style.display = "block";
}


// to allow early built-in module check
window.addEventListener("load", function () {
    init();
});


//////////////////
// WEBPACK FOOTER
// ./src/js/webplayer.js
// module id = 84
// module chunks = 0